import { EChart, LegendIcon } from '/b2b/common/components/Charts';
// @ts-check
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import {
    axisNameForTimeFormat,
    getTimeFormat,
    maxTimeInterval,
    timeLabelFormatter,
} from '/b2b/common/components/Charts/helpers';

import ConsentDashboardContext from '../ConsentDashboard.context';
import PropTypes from 'prop-types';
import WidgetContainer from '/b2b/common/components/Reporting/WidgetContainer';
import { escapeCSVValue } from '/b2b/common/helpers/CSV';
import { fetchConsentReportsDashboard } from '/b2b/consent/services';
import { formatChartTooltip } from '/b2b/consent/format';
import { makeDownloadBlob } from '/b2b/common/components/Reporting/helpers';
import merge from 'lodash/merge';
import moment from 'moment';
import { parseDate } from '/b2b/common/helpers/Date';
import { translate } from '/b2b/common/helpers/i18n';
import useBoundCallback from '/b2b/common/helpers/Hooks/useBoundCallback';
import { useTheme } from '@mui/material/styles';
import { withAsyncData } from '/b2b/common/hoc/withAsyncData';
import withRetranslate from '/b2b/common/hoc/withRetranslate';

export const REPORT_NAME = 'consentsOverTime';

/** @type {import('echarts').EChartsOption} */
const staticOptions = {
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'none',
        },
        appendTo: document.body,
    },
    legend: {
        icon: LegendIcon,
        orient: 'horizontal',
        top: 'top',
    },
    grid: {
        containLabel: false,
    },
    useUTC: true,
    xAxis: {
        axisLabel: {
            hideOverlap: true,
            showMaxLabel: false,
            showMinLabel: false,
        },
        axisLine: { onZero: false, show: true },
        axisTick: { show: false },
        minorTick: { show: false },
        nameGap: 32,
        nameLocation: 'middle',
        nameTextStyle: {
            verticalAlign: 'top',
        },
        splitLine: { show: false },
        type: 'time',
    },
    yAxis: {
        axisLabel: { align: 'right', margin: 0 },
        axisLine: { show: false },
        axisTick: { show: false },
        nameLocation: 'middle',
        nameRotate: 90,
        nameTextStyle: {
            verticalAlign: 'bottom',
        },
        splitLine: { show: true },
        splitNumber: 2,
        type: 'value',
    },
    series: [
        {
            name: 'common.visitors',
            type: 'line',
            emphasis: {
                focus: 'series',
            },
            dimensions: [
                { name: 'date', type: 'ordinal' },
                { name: 'totalVisitors', type: 'int' },
            ],
        },
        {
            name: 'common.consents',
            type: 'bar',
            emphasis: {
                focus: 'series',
            },
            dimensions: [
                { name: 'date', type: 'ordinal' },
                { name: 'totalConsents', type: 'int' },
            ],
        },
    ],
};

const csvColumnMap = {
    date: 'common.date',
    totalConsents: 'common.consents',
    totalVisitors: 'common.visitors',
};

/**
 * @param {Unpacked<CmpReportsData['consentsOverTime']>} row
 * @param {Array<keyof typeof csvColumnMap>} columnNames
 * @return {(string|number)[]}
 */
const processRow = (row, columnNames) => {
    return columnNames
        .reduce((acc, column) => {
            switch (column) {
                case 'date':
                    acc.push(moment.utc(parseDate(row?.[column])).format('YYYY-MM-DD'));
                    break;
                default:
                    acc.push(row?.[column] ?? 0);
                    break;
            }
            return acc;
        }, /** @type {(string|number)[]} */ ([]))
        .filter(val => val !== undefined);
};

/**
 * @param {CmpReportsData} data
 * @returns {Blob}
 */
function makeCSV(data) {
    const { consentsOverTime = [] } = data || { consentsOverTime: undefined };
    const format = getTimeFormat(data.dateRange[0], data.dateRange[1]);
    const columnNames = /** @type {Array<keyof typeof csvColumnMap>} */ (Object.keys(csvColumnMap));
    // String CSV
    const csvFile = consentsOverTime
        .reduce(
            (acc, row) => {
                const rowArray = processRow(row, columnNames);
                acc.push(rowArray.map(escapeCSVValue).join(','));
                return acc;
            },
            [
                columnNames
                    .map(column => {
                        switch (column) {
                            case 'date':
                                return translate(
                                    axisNameForTimeFormat(format) || csvColumnMap[column],
                                    { date: '' }
                                );
                            default:
                                return translate(csvColumnMap[column], { delimiter: ' - ' });
                        }
                    })
                    .map(escapeCSVValue)
                    .join(','),
            ]
        )
        .join('\n');
    return new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
}

export const ConsentsOverTime = props => {
    const { data: providedData, filters, loading: providedLoading, shouldRetranslate } = props;
    const hasJurisdictionFilters = filters?.jurisdictions?.length > 0;
    const { data, error, load, loading } = useContext(ConsentDashboardContext);
    /** @type {import('/b2b/common/theme').OsanoDefaultTheme} */
    const theme = useTheme();
    const { consentsOverTime = [], dateRange = [], totalRange = [], traffic = [] } = data || {};
    const consentExists = consentsOverTime.some(({ totalConsents }) => totalConsents > 0);
    const trafficExists = traffic.some(({ totalVisitors }) => totalVisitors > 0);
    const noResults =
        (consentsOverTime.length === 0 && traffic.length === 0) ||
        !(consentExists || (trafficExists && !hasJurisdictionFilters));
    const isLoading = loading || providedLoading;

    const echartsInstance = useRef(null);
    const onChartReady = useCallback(chart => {
        echartsInstance.current = chart;
    }, []);

    const memoOption = useMemo(() => {
        const minTotal = totalRange[0] || 0;
        let scale = 1;
        if (minTotal >= 1000) {
            scale = 1000;
        } else if (minTotal >= 1000000) {
            scale = 1000000;
        }
        const dates = consentsOverTime.map(({ date }) =>
            moment(parseDate(date)).format('YYYY-MM-DD')
        );
        const format = getTimeFormat(dateRange[0], dateRange[1]);
        return merge(
            {},
            staticOptions,
            /** @type {import('echarts').EChartsOption} */ ({
                legend: {
                    show: !hasJurisdictionFilters,
                    left: theme.spacing(1.5),
                    selected: {
                        'common.visitors': !hasJurisdictionFilters, // Hide visitors when jurisdictions are selected
                        'common.consents': true,
                    },
                },
                tooltip: {
                    formatter: formatChartTooltip(format),
                },
                grid: {
                    left: theme.spacing(8.5),
                    right: theme.spacing(4.5),
                },
                xAxis: {
                    name: translate(axisNameForTimeFormat(format), { date: '' }).trim(),
                    axisLabel: {
                        formatter: timeLabelFormatter(dateRange[0], dateRange[1]),
                        customValues: dates,
                    },
                    max: dateRange[1],
                    min: dateRange[0],
                    maxInterval: maxTimeInterval(dateRange[0], dateRange[1]),
                },
                yAxis: {
                    show: consentsOverTime.length + traffic.length > 0,
                    name: translate('common.consents'),
                    nameGap: parseInt(theme.spacing(5), 10),
                    axisLabel: {
                        formatter: value => {
                            switch (scale) {
                                case 1000:
                                    return `${value / scale}k`;
                                case 1000000:
                                    return `${value / scale}m`;
                                default:
                                    return value;
                            }
                        },
                    },
                },
                dataset: {
                    source: consentsOverTime,
                },
                series: /** @type {import('echarts').SeriesOption[]} */ (staticOptions.series).map(
                    series => {
                        const visible =
                            series.name !== 'common.visitors' || !hasJurisdictionFilters;
                        const newSeries = {
                            ...series,
                            name: translate(series.name),
                        };
                        if (!visible) {
                            newSeries.data = [];
                        }
                        return newSeries;
                    }
                ),
            })
        );
    }, [consentsOverTime, shouldRetranslate]);

    const fetchData = useBoundCallback(
        filters =>
            fetchConsentReportsDashboard({
                filters: {
                    ...filters,
                    requestedCharts: [REPORT_NAME],
                },
            }),
        [filters]
    );

    const helpers = useMemo(
        () => ({
            makeCSV,
            name: 'consents-over-time',
        }),
        []
    );
    const makeDownload = useBoundCallback(makeDownloadBlob, [echartsInstance, data, helpers]);

    useLayoutEffect(() => {
        providedData ? load(Promise.resolve(providedData)) : load(fetchData());
    }, [providedData]);

    return (
        <WidgetContainer
            disabled={noResults}
            downloadFormats={['csv', 'png']}
            echartsInstance={echartsInstance}
            error={error}
            title="consent.dashboard.charts.title.consentsOverTime"
            tooltip="consent.dashboard.charts.tooltip.consentsOverTime"
        >
            <EChart
                loading={isLoading}
                makeDownload={makeDownload}
                noResults={noResults}
                onChartReady={onChartReady}
                option={memoOption}
            />
        </WidgetContainer>
    );
};

ConsentsOverTime.propTypes = {
    data: PropTypes.object,
    filters: PropTypes.object,
    loading: PropTypes.bool,
    shouldRetranslate: PropTypes.string,
};

/** @type {import('/b2b/common/hoc/withAsyncData').ComponentWithOverriddenProps<CmpReportsData, { filters: CmpReportsQueryParams }>} */
const TranslatedComponent = withRetranslate(ConsentsOverTime);

export default withAsyncData(TranslatedComponent, ConsentDashboardContext);
